// pages/CollectionPage.js - 集中监控
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Row, Col, Table, Tag, Button, Modal, Form, Input, Select, message, Descriptions } from 'antd';
import { DashboardOutlined, ThunderboltOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { api } from '../services/api';

const cardStyle = {
  background: 'linear-gradient(135deg, #0d1f44 0%, #132c5e 100%)',
  border: '1px solid rgba(0, 180, 216, 0.3)',
  borderRadius: 8,
  boxShadow: '0 0 20px rgba(0, 180, 216, 0.1)',
};
const COLORS = { primary: '#00d4ff', text: '#ffffff', muted: 'rgba(255,255,255,0.6)' };

const CollectionPage = () => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({ total: 0, online: 0, todayPower: '0', totalPower: '0' });
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailDevice, setDetailDevice] = useState(null);
  const [form] = Form.useForm();

  const fetchStats = async () => {
    try {
      const json = await api.getDeviceStats();
      if (json.success) setStats({
        total: json.stats.total,
        online: parseFloat(json.stats.status[t('collection.running')] || 0),
        todayPower: parseFloat(json.stats.todayPower || 0).toFixed(1),
        totalPower: parseFloat(json.stats.totalPower || 0).toFixed(1)
      });
    } catch (err) { console.error(err); }
  };

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const json = await api.getDeviceList({ pageSize: 100 });
      if (json.success) setDevices(json.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchStats(); fetchDevices(); }, []);

  const statusColor = (t) => ({ [t('collection.running')]: 'green', [t('collection.standby')]: 'orange', [t('collection.offline')]: 'default', [t('collection.fault')]: 'red' });

  const columns = [
    { title: t('collection.deviceName'), dataIndex: 'name', key: 'name', render: val => <span style={{ color: COLORS.text }}>{val}</span> },
    { title: t('collection.type'), dataIndex: 'typeText', key: 'typeText', render: val => <Tag>{val}</Tag> },
    { title: t('collection.model'), dataIndex: 'model', key: 'model', render: val => <span style={{ color: COLORS.muted }}>{val || '-'}</span> },
    { title: t('collection.location'), dataIndex: 'location', key: 'location', render: val => <span style={{ color: COLORS.muted }}>{val || '-'}</span> },
    { title: t('collection.ratedPowerKw'), dataIndex: 'rated_power', key: 'rated_power', render: v => <span style={{ color: COLORS.primary }}>{v || '-'}</span> },
    { title: t('collection.status'), dataIndex: 'statusText', key: 'statusText', render: val => <Tag color={statusColor(t)[val]}>{val}</Tag> },
    { title: t('collection.todayPowerKwh'), dataIndex: 'today_power', key: 'today_power', render: v => <span style={{ color: COLORS.primary }}>{v || '-'}</span> },
    {
      title: t('collection.action'), key: 'action', render: (_, record) => (
        <>
          <Button type="link" style={{ color: COLORS.primary }} icon={<InfoCircleOutlined />} onClick={() => handleDetail(record)}>{t('collection.detail')}</Button>
          <Button type="link" style={{ color: COLORS.primary }} onClick={() => handleEdit(record)}>{t('collection.edit')}</Button>
          <Button type="link" danger onClick={() => handleDelete(record.id)}>{t('collection.delete')}</Button>
        </>
      )
    }
  ];

  const handleEdit = (record) => {
    setEditingDevice(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDetail = (record) => {
    setDetailDevice(record);
    setDetailVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: t('collection.confirmDelete'),
      content: t('collection.irreversible'),
      className: 'dark-confirm-modal',
      onOk: async () => {
        try {
          const json = await api.deleteDevice(id);
          if (json.success) { message.success(t('collection.deleteSuccess')); fetchDevices(); fetchStats(); }
          else message.error(json.message);
        } catch (err) { message.error(t('collection.deleteFailed')); }
      }
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const json = editingDevice
        ? await api.updateDevice(editingDevice.id, values)
        : await api.addDevice(values);
      if (json.success) {
        message.success(editingDevice ? t('collection.updateSuccess') : t('collection.addSuccess'));
        setModalVisible(false); form.resetFields(); setEditingDevice(null);
        fetchDevices(); fetchStats();
      } else { message.error(json.message); }
    } catch (err) { message.error(t('collection.operationFailed')); }
  };

  const statLabelStyle = { fontSize: 12, color: COLORS.muted };
  const statValueStyle = (color) => ({ fontSize: 24, fontWeight: 700, color });

  return (
    <div style={{ minHeight: '100%' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {[
          { label: t('collection.totalDevices'), value: stats.total, icon: <DashboardOutlined />, color: COLORS.primary },
          { label: t('collection.onlineDevices'), value: stats.online, icon: <ThunderboltOutlined />, color: '#52c41a' },
          { label: t('collection.todayGeneration'), value: `${stats.todayPower} kWh`, icon: <ThunderboltOutlined />, color: '#f4c542' },
          { label: t('collection.totalGeneration'), value: `${stats.totalPower} kWh`, icon: <DashboardOutlined />, color: '#52e8b0' },
        ].map(item => (
          <Col xs={24} sm={12} lg={6} key={item.label}>
            <Card variant="borderless" style={cardStyle} styles={{ body: { padding: '20px' } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, background: 'rgba(0,180,216,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(0,180,216,0.3)' }}>
                  {React.cloneElement(item.icon, { style: { fontSize: 22, color: item.color } })}
                </div>
                <div>
                  <div style={statLabelStyle}>{item.label}</div>
                  <div style={statValueStyle(item.color)}>{item.value}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 20 } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, letterSpacing: 2, paddingLeft: 12, borderLeft: '3px solid #00d4ff' }}>{t('collection.deviceList')}</div>
          <div>
            <Button icon={<ReloadOutlined />} onClick={() => { fetchStats(); fetchDevices(); }} style={{ marginRight: 8, background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>{t('collection.refresh')}</Button>
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => { setEditingDevice(null); form.resetFields(); setModalVisible(true); }}
              style={{ background: 'rgba(0,180,216,0.2)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.4)' }}>{t('collection.addDevice')}</Button>
          </div>
        </div>
        <Table columns={columns} dataSource={devices} loading={loading} rowKey="id" pagination={{ pageSize: 10 }}
          style={{ background: 'transparent' }}
        />
      </Card>

      <Modal
        title={editingDevice ? t('collection.editDevice') : t('collection.addDeviceTitle')}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => { setModalVisible(false); form.resetFields(); setEditingDevice(null); }}
        okText={t('common.confirm')} cancelText={t('common.cancel')}
        styles={{ body: { background: '#0d1f44', color: '#fff' }, header: { background: '#0d1f44', color: '#fff' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label={t('collection.deviceNameLabel')} rules={[{ required: true, message: t('collection.enterDeviceName') }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label={t('collection.deviceType')} rules={[{ required: true, message: t('collection.selectDeviceType') }]}>
            <Select>
              <Select.Option value="photovoltaic">{t('collection.photovoltaic')}</Select.Option>
              <Select.Option value="battery">{t('collection.battery')}</Select.Option>
              <Select.Option value="charger">{t('collection.charger')}</Select.Option>
              <Select.Option value="generator">{t('collection.generator')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="model" label={t('collection.model')}><Input /></Form.Item>
          <Form.Item name="location" label={t('collection.location')}><Input /></Form.Item>
          <Form.Item name="capacity" label={t('collection.capacityUnit')}><Input type="number" /></Form.Item>
          <Form.Item name="rated_power" label={t('collection.ratedPowerKw')}><Input type="number" /></Form.Item>
          {editingDevice && (
            <Form.Item name="status" label={t('collection.statusLabel')}>
              <Select>
                <Select.Option value={0}>{t('collection.offline')}</Select.Option>
                <Select.Option value={1}>{t('collection.running')}</Select.Option>
                <Select.Option value={2}>{t('collection.standby')}</Select.Option>
                <Select.Option value={3}>{t('collection.fault')}</Select.Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 设备详情 Modal */}
      <Modal title={t('collection.deviceDetail')} open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        className="dark-confirm-modal"
        styles={{ body: { background: '#0d1f44', color: '#fff' }, header: { background: '#0d1f44', color: '#fff' } }}
      >
        {detailDevice && (
          <div>
            <Descriptions column={2} size="small" bordered
              style={{ marginBottom: 16 }}
              labelStyle={{ color: 'rgba(255,255,255,0.6)', background: 'rgba(0,0,0,0.2)' }}
              contentStyle={{ color: '#fff', background: 'rgba(0,0,0,0.1)' }}
            >
              <Descriptions.Item label={t('collection.deviceName')}>{detailDevice.name}</Descriptions.Item>
              <Descriptions.Item label={t('collection.type')}>{detailDevice.typeText}</Descriptions.Item>
              <Descriptions.Item label={t('collection.model')}>{detailDevice.model || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('collection.location')}>{detailDevice.location || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('collection.ratedPowerKw')}>{detailDevice.rated_power ? `${detailDevice.rated_power} kW` : '-'}</Descriptions.Item>
              <Descriptions.Item label={t('collection.capacity')}>{detailDevice.capacity ? `${detailDevice.capacity} kW/kWh` : '-'}</Descriptions.Item>
              <Descriptions.Item label={t('collection.status')}>{detailDevice.statusText}</Descriptions.Item>
              <Descriptions.Item label={t('collection.todayPower')}>{detailDevice.today_power ? `${detailDevice.today_power} kWh` : '-'}</Descriptions.Item>
              <Descriptions.Item label={t('collection.totalPower')} span={2}>{detailDevice.total_power ? `${detailDevice.total_power} kWh` : '-'}</Descriptions.Item>
            </Descriptions>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={() => setDetailVisible(false)}
                style={{ background: 'rgba(0,0,0,0.2)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,180,216,0.3)' }}>
                {t('collection.close')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CollectionPage;
